import type { MetaFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";
import { SEOLayout } from "~/components/seo/SEOLayout";
import { ContactEmail } from "~/components/contact-email";
import { getSeoMeta } from "~/utils/seo-meta";

const fontClass = "font-['Public Sans'] font-sans";

export const meta: MetaFunction = () =>
  getSeoMeta({
    title: "Terms & Conditions - Prizo",
    description:
      "Terms & Conditions for Prizo — the terms of service and usage policies for our Shopify gamification app.",
    url: "/terms",
  });

export default function Terms() {
  return (
    <SEOLayout>
      <div className="bg-[#ffffff] min-h-screen w-full">
        <div className="max-w-[1440px] mx-auto px-4 md:px-[120px] py-10 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1
                className={`${fontClass} font-bold text-[32px] md:text-[48px] text-[#2c2c2c] mb-4`}
              >
                Terms & Conditions
              </h1>
              <p
                className={`${fontClass} text-[16px] md:text-[18px] text-[#6a6a6a] max-w-2xl mx-auto`}
              >
                Please read these Terms & Conditions carefully before using Prizo. By using our
                service, you agree to be bound by these terms.
              </p>
              <p className={`${fontClass} text-[14px] text-[#6a6a6a] mt-4`}>
                Last updated: January 1, 2025
              </p>
            </div>

            <div className="prose prose-lg max-w-none">
              <div className="space-y-8">
                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    1. Acceptance of Terms
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      By accessing and using Prizo ("the Service"), you accept and agree to be
                      bound by the terms and provision of this agreement. If you do not agree to
                      abide by the above, please do not use this service.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    2. Service Description
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      Prizo is a gamification platform that enables businesses to create
                      interactive post-purchase experiences for their customers. Our service
                      includes:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Interactive mini-games and challenges</li>
                      <li>Reward and prize management systems</li>
                      <li>Customer engagement analytics</li>
                      <li>Integration with e-commerce platforms</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    3. User Accounts and Responsibilities
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      When you create an account with us, you must provide information that is
                      accurate, complete, and current at all times. You are responsible for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Safeguarding your account password</li>
                      <li>All activities that occur under your account</li>
                      <li>Ensuring compliance with applicable laws and regulations</li>
                      <li>Using the service in accordance with these terms</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    4. Acceptable Use Policy
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>You agree not to use the service to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Violate any applicable laws or regulations</li>
                      <li>Transmit harmful, offensive, or inappropriate content</li>
                      <li>Attempt to gain unauthorized access to our systems</li>
                      <li>Interfere with or disrupt the service</li>
                      <li>Use the service for fraudulent or deceptive purposes</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    5. Payment and Billing
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>For paid services:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>All fees are non-refundable unless otherwise specified</li>
                      <li>You authorize us to charge your payment method for all applicable fees</li>
                      <li>We may change our pricing with 30 days advance notice</li>
                      <li>Late payments may result in service suspension</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    6. Intellectual Property
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      The service and its original content, features, and functionality are owned
                      by Prizo and are protected by international copyright, trademark, patent,
                      trade secret, and other intellectual property laws.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    7. Service Availability
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      We strive to maintain high service availability, but we do not guarantee
                      uninterrupted service. We may suspend or terminate the service for
                      maintenance, updates, or other operational reasons.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    8. Limitation of Liability
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      In no event shall Prizo be liable for any indirect, incidental, special,
                      consequential, or punitive damages, including without limitation, loss of
                      profits, data, use, goodwill, or other intangible losses.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    9. Termination
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      We may terminate or suspend your account and access to the service
                      immediately, without prior notice, for conduct that we believe violates
                      these Terms & Conditions or is harmful to other users, us, or third
                      parties.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    10. Changes to Terms
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      We reserve the right to modify these terms at any time. We will notify
                      users of any material changes via email or through the service. Your
                      continued use of the service after such modifications constitutes
                      acceptance of the updated terms.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    11. Contact Information
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      If you have any questions about these Terms & Conditions, please contact
                      us:
                    </p>
                    <ul className="list-none space-y-2">
                      <li>Email: <ContactEmail /></li>
                      <li>
                        Website:{" "}
                        <Link to="/" className="text-[#696cff] hover:underline">
                          prizo.io
                        </Link>
                      </li>
                    </ul>
                  </div>
                </section>
              </div>
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/"
                className={`${fontClass} inline-flex items-center px-8 py-4 bg-[#696cff] text-white rounded-2xl font-medium hover:bg-[#5a5ce6] transition-colors`}
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SEOLayout>
  );
}
